import { ArrowUp, Mail, MapPin, PhoneCall } from "lucide-react";
import { Link } from "react-router-dom";
import {
  brand,
  contactInfo,
  footerNavigation,
  socialLinks
} from "../../data/navigation";

/* Inline SVG icons for social platforms not available in this lucide-react version */
function InstagramIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
    </svg>
  );
}

function LinkedinIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
      <rect x="2" y="9" width="4" height="12" />
      <circle cx="4" cy="4" r="2" />
    </svg>
  );
}

function SocialIcon({ platform, className = "h-4 w-4" }) {
  if (platform === "instagram") return <InstagramIcon className={className} />;
  if (platform === "linkedin") return <LinkedinIcon className={className} />;
  return null;
}

function Footer() {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <footer className="bg-[#000a1e] text-white">
      <div className="mx-auto w-full max-w-7xl px-5 pb-8 pt-20 sm:px-6 lg:px-10">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-[1.3fr_1fr_1fr_1.2fr]">
          <div>
            <Link className="inline-flex items-center gap-3" to="/">
              <img
                src="/TCS TCS.png"
                alt={`${brand.name} Logo`}
                className="h-28 w-auto object-contain transition-all duration-300"
                style={{
                  filter: "brightness(0) saturate(100%) invert(70%) sepia(61%) saturate(795%) hue-rotate(345deg) brightness(98%) contrast(92%)"
                }}
              />
              <span className="text-xl font-semibold uppercase tracking-tight text-brand-gold">
                {brand.name}
              </span>
            </Link>
            <p className="mt-5 max-w-sm text-sm leading-relaxed text-slate-300/90">{brand.summary}</p>
            <p className="mt-6 text-xs uppercase tracking-[0.2em] text-brand-gold/85">{brand.trustLine}</p>

            {/* Social Links */}
            <div className="mt-6 flex items-center gap-3">
              {socialLinks.map((social) => (
                <a
                  key={social.platform}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-white/15 text-slate-300 transition-all duration-300 hover:border-brand-gold/50 hover:bg-brand-gold/10 hover:text-brand-gold"
                >
                  <SocialIcon platform={social.platform} />
                </a>
              ))}
              <span className="text-xs text-slate-500">Follow us</span>
            </div>
          </div>

          <div>
            <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-white/75">Quick Links</h3>
            <ul className="mt-5 space-y-3.5">
              {footerNavigation.quickLinks.map((item) => (
                <li key={item.to}>
                  <Link className="text-sm text-slate-300 transition-colors hover:text-white" to={item.to}>
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-white/75">Services</h3>
            <ul className="mt-5 space-y-3.5">
              {footerNavigation.serviceLinks.map((item) => (
                <li key={item.label || item.to}>
                  {item.external ? (
                    <a
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm text-slate-300 transition-colors hover:text-brand-gold"
                    >
                      {item.label}
                      <span className="rounded bg-brand-gold/20 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-brand-gold">
                        Abroad
                      </span>
                    </a>
                  ) : (
                    <Link className="text-sm text-slate-300 transition-colors hover:text-white" to={item.to}>
                      {item.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-white/75">Contact</h3>
            <ul className="mt-5 space-y-3.5 text-sm text-slate-300">
              <li className="flex items-start gap-2.5">
                <MapPin className="mt-0.5 h-4 w-4 text-brand-gold" />
                <span>{contactInfo.address}</span>
              </li>
              <li className="flex items-center gap-2.5">
                <Mail className="h-4 w-4 text-brand-gold" />
                <a href={`mailto:${contactInfo.email}`} className="hover:text-white">
                  {contactInfo.email}
                </a>
              </li>
              <li className="flex items-center gap-2.5">
                <PhoneCall className="h-4 w-4 text-brand-gold" />
                <a href={`tel:${contactInfo.phoneHref}`} className="hover:text-white">
                  {contactInfo.phoneDisplay}
                </a>
              </li>
            </ul>

            {/* Social in Contact column */}
            <div className="mt-6">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/50">Connect With Us</p>
              <div className="mt-3 flex flex-col gap-2.5">
                {socialLinks.map((social) => (
                  <a
                    key={social.platform}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm text-slate-300 transition-colors hover:text-brand-gold"
                  >
                    <SocialIcon platform={social.platform} />
                    {social.label}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-start justify-between gap-4 border-t border-white/10 pt-6 sm:flex-row sm:items-center">
          <p className="text-xs text-slate-400">
            © {new Date().getFullYear()} TheCareerSpace. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            {socialLinks.map((social) => (
              <a
                key={social.platform}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={social.label}
                className="text-slate-500 transition-colors hover:text-brand-gold"
              >
                <SocialIcon platform={social.platform} />
              </a>
            ))}
            <button
              onClick={scrollToTop}
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-brand-gold transition-colors hover:text-brand-gold-soft"
              aria-label="Back to top"
            >
              <ArrowUp className="h-3.5 w-3.5" />
              Back to Top
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
